import { inject, Injectable, LOCALE_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Evaluee } from '@career-scope/models';
import { AUDIO } from './audio-data';
import { EvalueeService } from './evaluee.service';
import { SpeechService } from './speech-synthesis';
import { filter, take } from 'rxjs/operators';
import { FeaturesSecurityService } from './features-security.service';

@Injectable({
  providedIn: 'root'
})
export class AudioService {
  locale = inject(LOCALE_ID);
  currentUrl = "";

  constructor(private router: Router, private speechService: SpeechService, private es: EvalueeService, private fss: FeaturesSecurityService) {
    this.initAudio();
  }

  
  initAudio() {
    console.log("initAudio() subscribed");   // <--- ADD 1
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentUrl = event.urlAfterRedirects || event.url || '';
        setTimeout(() => this.startAudio(), 150);
      });

    // Trigger audio on initial page load
    this.currentUrl = this.router.url;
    this.startAudio();

  }

  public displayControls(show: boolean) {
    this.es.saveAudioEnabled(show);
    this.fss.setAudioEnabled(show);
    if (show) {
      this.startAudio();
    }
    if (!show) {
      this.stopAudio();
    }
  }

  private normalizeUrl(url: string): string {
    // Remove query params / fragments
    url = url.split('?')[0].split('#')[0];

    if (url.startsWith('/')) {
      url = url.substring(1);
    }

    const parts = url.split('/');

    if (['en', 'es-US', 'es', 'fr', 'de'].includes(parts[0])) {
      return '/' + parts.slice(1).join('/');
    }

    return '/' + url;
  }

  private startAudio() {
    // ensure nothing is speaking
    speechSynthesis.cancel();

    console.log("currentUrl =", this.currentUrl);

    const evaluee = this.es.evaluee.value;
    console.log("evaluee =", evaluee);

    const enabled = this.es.evaluee.value?.audioEnabled;
    console.log("Audio enabled =", enabled);

    if (!evaluee) {
      console.log("No evaluee → CANNOT PLAY AUDIO");
      return;
    }

    if (!enabled) {
      console.log("Audio disabled → CANNOT PLAY AUDIO");
      return;
    }

    const normalized = this.normalizeUrl(this.currentUrl);
    console.log("normalized URL =", normalized);

    const text = this.getAudio(normalized, evaluee);
    console.log("Audio text =", text);

    if (!text) {
      console.log("No audio text found for this URL");
      return;
    }

    console.log("Speaking now…");

    this.speak(text, true);
  }

  speak(text: string, stopCurrent: boolean) {
    console.log('speaking', text);
    this.speechService.speak(text, stopCurrent);
  }

  repeat() {
    this.speechService.repeat();
  }

  stopAudio() {
    this.speechService.stop();
  }

  getAudio(url: string, evaluee: Evaluee | null | undefined): string {
    const consumerDirectEvaluee = evaluee?.consumerDirect || false;

    if (url === '/interests') {
      if (evaluee?.interestComplete && !evaluee?.exercisesComplete) {
        return this.getAudioText(url, 2, consumerDirectEvaluee);
      }
      if (evaluee?.interestComplete && evaluee?.exercisesComplete) {
        return this.getAudioText(url, 3, consumerDirectEvaluee);
      }
      return this.getAudioText(url, 1, consumerDirectEvaluee);
    }

    if (url === '/exercises') {
      if (evaluee?.exercisesComplete && !evaluee?.interestComplete) {
        return this.getAudioText(url, 2, consumerDirectEvaluee);
      }
      if (evaluee?.exercisesComplete && evaluee?.interestComplete) {
        return this.getAudioText(url, 3, consumerDirectEvaluee);
      }
      return this.getAudioText(url, 1, consumerDirectEvaluee);
    }

    if (url === '/results') {
      if (!evaluee?.exercisesComplete || !evaluee?.interestComplete) {
        return this.getAudioText(url, 1, consumerDirectEvaluee);
      }
      if (evaluee.assessmentSettings?.hideResultOnCompletion) {
        return this.getAudioText(url, 3, consumerDirectEvaluee);
      }
      return this.getAudioText(url, 2, consumerDirectEvaluee);
    }

    if (url.includes('apttest')) {
      return this.getAudioText(url, 1, consumerDirectEvaluee);
    }

    return this.getAudioText(url, null, consumerDirectEvaluee);
  }

  getAudioText(url: string, secondary: number | null, consumerDirect = false): string {
    const audio = AUDIO.find(a => a.url === url && (secondary ? a.secondary === secondary : true));

    if (this.locale === 'en') {
      return consumerDirect ? audio?.consumerDirectText || audio?.text || '' : audio?.text || '';
    }

    return consumerDirect ? audio?.consumerDirectSpanishText || audio?.spanishText || '' : audio?.spanishText || '';
  }

}
